import { createSkyInteriorAtmosphere } from './189-sky-interior-atmosphere.mjs';
import { createSkyVolumeAtmosphere } from './214-sky-volume-atmosphere.mjs';

/** Peripheral atmosphere has one fixed24-quad pool (48triangles/one draw),
 * no textures, lights, emission queue or private clock. Full projected quad
 * area is below4% of the viewport; soft alpha lowers its actual coverage.
 * It never represents a hazard, reward, physical wind or target. */
export const SKY_ATMOSPHERE_BUDGET = Object.freeze({
  full: 24,
  balanced: 12,
  masks: 12,
  cloudWidth: 0.2,
  cloudHeight: 0.065,
  windWidth: 0.022,
  windHeight: 0.11,
});

/** Sample an absolute active-time ribbon cycle. Each pooled identity fades to
 * zero before recycling; pauses/rewinds need no catch-up or particle backlog.
 * Coordinates/sizes are NDC, deliberately outside the central action window. */
export function skyAtmospherePose(index, time) {
  const cloud = index % 3 === 0,
    side = index % 2 ? -1 : 1,
    duration = cloud ? 4.8 : 1.6,
    phase = (((time / duration + index * 0.61803398875) % 1) + 1) % 1,
    fade = Math.sin(Math.PI * phase) ** 2;
  return {
    x: side * (0.77 + 0.09 * Math.sin(phase * Math.PI * 2 + index)),
    y: -0.58 + ((index * 0.237) % 1) * 1.16 + (phase - 0.5) * (cloud ? 0.16 : 0.32),
    width: cloud ? SKY_ATMOSPHERE_BUDGET.cloudWidth : SKY_ATMOSPHERE_BUDGET.windWidth,
    height: cloud ? SKY_ATMOSPHERE_BUDGET.cloudHeight : SKY_ATMOSPHERE_BUDGET.windHeight,
    opacity: fade * (cloud ? 0.2 : 0.3),
    cloud,
  };
}

/** Own a bounded screen-edge cloud/wind layer. Final-camera target masks keep
 * actual craft/bolts clear, and overflow omits decoration rather than dropping
 * a safety mask. Active boss aiming, interiors, endings, reduced motion and the
 * minimal quality tier omit this layer entirely. Cache-owned art is untouched. */
export function createSkyAtmosphere(T, scene) {
  const interior = createSkyInteriorAtmosphere(T, scene);
  const masks = Array.from({ length: SKY_ATMOSPHERE_BUDGET.masks }, () => new T.Vector3(4, 4, 0)),
    geometry = new T.PlaneGeometry(1, 1),
    opacity = new T.InstancedBufferAttribute(new Float32Array(SKY_ATMOSPHERE_BUDGET.full), 1),
    kind = new T.InstancedBufferAttribute(new Float32Array(SKY_ATMOSPHERE_BUDGET.full), 1);
  const volumes = createSkyVolumeAtmosphere(T, scene, masks);
  geometry.setAttribute('wispOpacity', opacity);
  geometry.setAttribute('wispKind', kind);
  const material = new T.ShaderMaterial({
    uniforms: { masks: { value: masks }, maskCount: { value: 0 }, gold: { value: 0 } },
    transparent: true,
    depthWrite: false,
    depthTest: false,
    toneMapped: false,
    vertexShader: `
      attribute float wispOpacity;
      attribute float wispKind;
      varying vec2 vUv;
      varying vec2 vScreen;
      varying float vOpacity;
      varying float vKind;
      void main() {
        vUv = uv; vOpacity = wispOpacity; vKind = wispKind;
        vec4 p = instanceMatrix * vec4(position, 1.0);
        vScreen = p.xy;
        gl_Position = vec4(p.xy, 0.0, 1.0);
      }`,
    fragmentShader: `
      uniform vec3 masks[${SKY_ATMOSPHERE_BUDGET.masks}];
      uniform int maskCount;
      uniform float gold;
      varying vec2 vUv;
      varying vec2 vScreen;
      varying float vOpacity;
      varying float vKind;
      void main() {
        if (abs(vScreen.x) < 0.62 && abs(vScreen.y) < 0.72) discard;
        float safety = 1.0;
        for (int i = 0; i < ${SKY_ATMOSPHERE_BUDGET.masks}; i++) {
          if (i >= maskCount) break;
          safety *= smoothstep(masks[i].z, masks[i].z + 0.06, distance(vScreen, masks[i].xy));
        }
        vec2 p = vUv * 2.0 - 1.0;
        float soft = pow(max(0.0, 1.0 - dot(p, p)), 2.0);
        float bend = sin(vUv.y * 3.14159) * 0.45;
        float stream = pow(max(0.0, 1.0 - abs(p.x - bend)), 3.0);
        stream *= smoothstep(0.0, 0.2, vUv.y) * (1.0 - smoothstep(0.65, 1.0, vUv.y));
        float alpha = mix(stream, soft, vKind) * vOpacity * safety;
        if (alpha < 0.005) discard;
        vec3 color = mix(vec3(0.72, 0.86, 0.9), vec3(0.94, 0.85, 0.67), gold);
        gl_FragColor = vec4(color, alpha);
      }`,
  });
  const mesh = new T.InstancedMesh(geometry, material, SKY_ATMOSPHERE_BUDGET.full),
    matrix = new T.Matrix4(),
    point = new T.Vector3();
  mesh.name = 'sky-peripheral-atmosphere';
  mesh.frustumCulled = false;
  mesh.renderOrder = 4;
  mesh.visible = false;
  mesh.count = 0;
  scene.add(mesh);
  let current = null,
    dead = false;
  return {
    draw(state, stage, decoration, quiet) {
      interior.draw(state, decoration, quiet);
      volumes.draw(state, stage, decoration, quiet);
      current = state;
      const phase = state.leviathan?.encounter?.phase,
        bossAction = state.boss && !state.traversal && state.boss.controlMode !== 'flyby';
      mesh.visible =
        !dead &&
        !quiet &&
        decoration.tier < 2 &&
        ['playing', 'paused'].includes(state.status) &&
        state.sector < 2 &&
        stage.artVariant !== 'first-dragon' &&
        stage.encounterId !== 'gilded-leviathan' &&
        !bossAction &&
        (!phase || phase === 'arrival') &&
        Number.isFinite(state.time);
      mesh.count = mesh.visible
        ? decoration.tier === 0
          ? SKY_ATMOSPHERE_BUDGET.full
          : SKY_ATMOSPHERE_BUDGET.balanced
        : 0;
      if (!mesh.visible) return;
      material.uniforms.gold.value = stage.encounterId === 'gilded-leviathan' ? 1 : 0;
      for (let index = 0; index < mesh.count; index++) {
        const pose = skyAtmospherePose(index, state.time);
        matrix.makeScale(pose.width, pose.height, 1);
        matrix.setPosition(pose.x, pose.y, 0);
        mesh.setMatrixAt(index, matrix);
        opacity.setX(index, pose.opacity);
        kind.setX(index, pose.cloud ? 1 : 0);
      }
      mesh.instanceMatrix.needsUpdate = true;
      opacity.needsUpdate = true;
      kind.needsUpdate = true;
    },
    cull(camera) {
      if ((!mesh.visible && !volumes.visible) || !current) return;
      camera.updateMatrixWorld(true);
      let count = 0;
      for (const item of [...(current.enemies ?? []), ...(current.threats ?? [])]) {
        if (item.defeated || item.hp <= 0 || item.collisionEnabled === false) continue;
        if (![item.x, item.y, item.z].every(Number.isFinite)) continue;
        point.set(item.x, item.y, item.z).applyMatrix4(camera.matrixWorldInverse);
        const depth = -point.z,
          radius = Number.isFinite(item.radius) && item.radius > 0 ? item.radius : 1,
          projectedRadius =
            (radius *
              Math.max(camera.projectionMatrix.elements[0], camera.projectionMatrix.elements[5])) /
            Math.max(camera.near, depth - radius);
        point.applyMatrix4(camera.projectionMatrix);
        if (Math.abs(point.z) >= 1 || Math.abs(point.x) > 1.3 || Math.abs(point.y) > 1.3) continue;
        if (count === masks.length) {
          mesh.visible = false;
          mesh.count = 0;
          volumes.cull(camera, count, true);
          return;
        }
        masks[count++].set(point.x, point.y, Math.max(0.12, projectedRadius) + 0.05);
      }
      material.uniforms.maskCount.value = count;
      volumes.cull(camera, count);
    },
    dispose() {
      if (dead) return;
      dead = true;
      interior.dispose();
      volumes.dispose();
      current = null;
      mesh.removeFromParent();
      mesh.dispose();
      geometry.dispose();
      material.dispose();
    },
  };
}
